import { User } from "../types/user.d";
import axios from "../utils/axiosInstance";

/**
 * Service for handling authentication-related API calls.
 */
export const AuthService = {
  /**
   * Logs in the user with the provided credentials.
   * @param {string} username - Username of the user.
   * @param {string} password - Password of the user.
   * @returns {Promise<LoginResponse>} - Response containing the authentication token.
   * @throws Will throw an error if the login fails.
   */
  async login(username: string, password: string): Promise<User> {
    try {
      const response = await axios.post<User>("/Auth/connect", {
        username,
        password,
      });
      return response.data;
    } catch (error: any) {
      const errorData = error.response?.data;
      if (errorData) {
        throw errorData;
      }
      throw new Error(error.message || "Erreur lors de la connexion");
    }
  },

  /**
   * Logs out the currently authenticated user.
   * @returns {Promise<void>} - Resolves if the logout is successful.
   * @throws Will throw an error if the logout fails.
   */
  async logout(): Promise<void> {
    try {
      await axios.post("/Auth/logout", {});
    } catch (error: any) {
      throw error.response?.data || new Error("Erreur lors de la déconnexion");
    }
  },

  /**
   * Retrieves the currently authenticated user's information.
   * @returns {Promise<{ username: string }>} - Object containing the username of the current user.
   * @throws Will throw an error if the request fails.
   */
  async getCurrentUser(): Promise<User> {
    try {
      const response = await axios.get<User>(`/UserInfo/me`);
      return response.data;
    } catch (error: any) {
      throw (
        error.response?.data ||
        new Error("Erreur lors de la récupération de l’utilisateur courant")
      );
    }
  },

  /**
   * 
   * @returns
   * @throws
   */
  async refreshToken(): Promise<void> {
    try {
      await axios.post(`/Auth/refresh`, {});
    } catch (error: any) {
      throw (
        error.response?.data || new Error("Erreur lors du rafraîchissement")
      );
    }
  },
};

export default AuthService;
